import React from "react";
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, TrendingUp, Cpu, User } from 'lucide-react';

interface MarketSignalsRowProps {
  monetizationClarity: number; // 0-100
  demandSignal: number; // 0-100
  technicalRisk: number; // 0-100 (higher = riskier)
  founderAdvantage: number; // 0-100
}

const getSignalLevel = (value: number): { label: string; color: string } => {
  if (value >= 70) return { label: "High", color: "bg-green-500/10 text-green-700 border-green-500/30" };
  if (value >= 40) return { label: "Medium", color: "bg-yellow-500/10 text-yellow-700 border-yellow-500/30" };
  return { label: "Low", color: "bg-red-500/10 text-red-700 border-red-500/30" };
};

const getRiskLevel = (value: number): { label: string; color: string } => {
  if (value <= 30) return { label: "Low", color: "bg-green-500/10 text-green-700 border-green-500/30" };
  if (value <= 60) return { label: "Medium", color: "bg-yellow-500/10 text-yellow-700 border-yellow-500/30" };
  return { label: "High", color: "bg-red-500/10 text-red-700 border-red-500/30" };
};

export function MarketSignalsRow({
  monetizationClarity,
  demandSignal,
  technicalRisk,
  founderAdvantage
}: MarketSignalsRowProps) {
  const monetization = getSignalLevel(monetizationClarity);
  const demand = getSignalLevel(demandSignal);
  const risk = getRiskLevel(technicalRisk);
  const advantage = getSignalLevel(founderAdvantage);

  const signals = [
    {
      icon: DollarSign,
      label: "Monetization Clarity",
      level: monetization,
      value: monetizationClarity
    },
    {
      icon: TrendingUp,
      label: "Demand Signal",
      level: demand,
      value: demandSignal
    },
    {
      icon: Cpu,
      label: "Technical Risk",
      level: risk,
      value: technicalRisk
    },
    {
      icon: User,
      label: "Founder Advantage",
      level: advantage,
      value: founderAdvantage
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {signals.map((signal, idx) => (
        <div 
          key={idx}
          className="flex items-center gap-2.5 p-3 rounded-xl bg-card border border-border/50 hover:border-border transition-colors"
        >
          <signal.icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground truncate">{signal.label}</p>
            <Badge 
              variant="outline" 
              className={`text-xs mt-0.5 ${signal.level.color}`}
            >
              {signal.level.label}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}
